import { Injectable, inject, signal } from '@angular/core';
import { Observable, map, of, forkJoin, switchMap, catchError } from 'rxjs';
import { Chat, ChatParticipant, Message, User } from 'ngx-chat';
import * as uuid from 'uuid';
import { environment as env } from '../../../environments/environment';
import { HttpService } from '../../shared/services/http.service';
import { ApiResponse } from '../../shared/models/api-response.model';
import { AuthService } from '../../shared/services/auth.service';
import {
  ApiChatContact,
  ApiConversationListItem,
  ApiCreateConversationRequest,
  ApiCursorMeta,
  ApiMarkAsReadResponse,
  ApiMessage,
  ApiMessagesListResponse,
  ApiParticipant,
  ApiSendMessageRequest,
  ApiUserSummary
} from '../../core/models/chat/chat-api.model';

export interface UsersPageResult {
  users: User[];
  total?: number;
  lastPage?: number;
}

export interface ContactsPageResult {
  users: User[];
  contacts: ApiChatContact[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface ContactsApiResponseEnvelope {
  data?: ApiChatContact[];
  meta?: ApiCursorMeta;
}

export interface MessagesPageResult {
  messages: Message[];
  hasMore: boolean;
}

interface MessagesApiResponseEnvelope {
  data?: ApiMessage[] | { data?: ApiMessage[] };
  meta?: { has_more?: boolean };
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private readonly baseUrl = `${env.baseUrl}chat/conversations`;

  private readonly httpService = inject(HttpService);
  private readonly authService = inject(AuthService);
  private readonly avatarBlobCache = new Map<string, string>();

  // Contatore reattivo delle conversazioni con messaggi non letti
  public readonly unreadConversationsCount = signal<number>(0);
  public readonly activeChatId = signal<string | null>(null);
  private readonly unreadConversationIds = new Set<string>();

  private currentUserId: string | null = null;
  private readonly OWN_MSG_IDS_STORAGE_KEY = 'obe_chat_own_msg_ids';
  private readonly OWN_MSG_UUIDS_STORAGE_KEY = 'obe_chat_own_msg_uuids';
  private readonly ownMessageIds = new Set<string>();
  private readonly ownMessageUuids = new Set<string>();

  public setCurrentUserId(id: string | number | null | undefined): void {
    if (id != null && String(id) !== 'undefined' && String(id).trim()) {
      this.currentUserId = String(id);
      this.loadOwnMessageTracking();
    }
  }

  public getCurrentUserId(): string | null {
    if (this.currentUserId) {
      return this.currentUserId;
    }
    const user = this.authService.getUser();
    return user?.id != null ? String(user.id) : null;
  }

  private loadOwnMessageTracking(): void {
    if (!this.currentUserId) return;
    try {
      const userKeySuffix = `_${this.currentUserId}`;
      const savedIds = localStorage.getItem(`${this.OWN_MSG_IDS_STORAGE_KEY}${userKeySuffix}`);
      if (savedIds) {
        const arr = JSON.parse(savedIds);
        if (Array.isArray(arr)) {
          arr.forEach((item) => this.ownMessageIds.add(String(item)));
        }
      }
      const savedUuids = localStorage.getItem(`${this.OWN_MSG_UUIDS_STORAGE_KEY}${userKeySuffix}`);
      if (savedUuids) {
        const arr = JSON.parse(savedUuids);
        if (Array.isArray(arr)) {
          arr.forEach((item) => this.ownMessageUuids.add(String(item)));
        }
      }
    } catch {
      // Ignora errori di parsing
    }
  }

  public rememberOwnMessage(id?: string | number, clientUuid?: string): void {
    let changed = false;
    if (id != null && String(id) !== 'undefined') {
      const idStr = String(id);
      if (!this.ownMessageIds.has(idStr)) {
        this.ownMessageIds.add(idStr);
        changed = true;
      }
    }
    if (clientUuid && clientUuid.trim()) {
      const uuidStr = clientUuid.trim();
      if (!this.ownMessageUuids.has(uuidStr)) {
        this.ownMessageUuids.add(uuidStr);
        changed = true;
      }
    }

    if (changed && this.currentUserId) {
      try {
        const userKeySuffix = `_${this.currentUserId}`;
        localStorage.setItem(
          `${this.OWN_MSG_IDS_STORAGE_KEY}${userKeySuffix}`,
          JSON.stringify(Array.from(this.ownMessageIds))
        );
        localStorage.setItem(
          `${this.OWN_MSG_UUIDS_STORAGE_KEY}${userKeySuffix}`,
          JSON.stringify(Array.from(this.ownMessageUuids))
        );
      } catch {
        // Ignora
      }
    }
  }

  public isOwnMessageIdOrUuid(id?: string | number, clientUuid?: string): boolean {
    if (id != null && this.ownMessageIds.has(String(id))) {
      return true;
    }
    if (clientUuid && clientUuid.trim()) {
      const trimmedUuid = clientUuid.trim();
      if (this.ownMessageUuids.has(trimmedUuid)) {
        return true;
      }
      // Se condivide i primi 18 caratteri con un proprio client_uuid registrato
      if (trimmedUuid.length >= 18) {
        const prefix = trimmedUuid.substring(0, 18);
        for (const ownUuid of this.ownMessageUuids) {
          if (ownUuid.startsWith(prefix)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  public updateUnreadCountFromChats(
    chats: { id: string | number; unreadCount?: number }[],
    reset = false
  ): void {
    if (reset) {
      this.unreadConversationIds.clear();
    }
    chats.forEach((c) => {
      const idStr = String(c.id);
      if (c.unreadCount && c.unreadCount > 0) {
        this.unreadConversationIds.add(idStr);
      } else {
        this.unreadConversationIds.delete(idStr);
      }
    });
    this.unreadConversationsCount.set(this.unreadConversationIds.size);
  }

  public markConversationAsReadLocal(chatId: string | number): void {
    const idStr = String(chatId);
    if (this.unreadConversationIds.has(idStr)) {
      this.unreadConversationIds.delete(idStr);
      this.unreadConversationsCount.set(this.unreadConversationIds.size);
    }
  }

  public incrementUnreadForConversation(chatId: string | number): void {
    const idStr = String(chatId);
    if (!this.unreadConversationIds.has(idStr)) {
      this.unreadConversationIds.add(idStr);
      this.unreadConversationsCount.set(this.unreadConversationIds.size);
    }
  }

  public refreshUnreadCount(): Observable<number> {
    return this.httpService
      .get<ApiConversationListItem[]>(`${this.baseUrl}?limit=10`, {
        skipLoader: true,
        skipError: true
      })
      .pipe(
        map((res) => {
          const rawList: ApiConversationListItem[] = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(
                  (res as unknown as { data?: { data?: ApiConversationListItem[] } })?.data?.data
                )
              ? (res as unknown as { data: { data: ApiConversationListItem[] } }).data.data
              : Array.isArray(res)
                ? (res as unknown as ApiConversationListItem[])
                : [];

          this.unreadConversationIds.clear();
          rawList.forEach((conv) => {
            if (conv.unread_count && conv.unread_count > 0) {
              this.unreadConversationIds.add(String(conv.id));
            }
          });
          const count = this.unreadConversationIds.size;
          this.unreadConversationsCount.set(count);
          return count;
        }),
        catchError(() => of(this.unreadConversationsCount()))
      );
  }

  /**
   * Scarica l'avatar protetto tramite HttpClient (inviando il Bearer token)
   * e genera un blob URL locale per visualizzarlo nei tag <img> senza errori 403.
   */
  resolveAvatarBlob(url: string | null | undefined): Observable<string | undefined> {
    if (!url) return of(undefined);
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      return of(url);
    }

    const convMatch = url.match(/\/conversations\/(\d+)\/avatar/);
    const convKey = convMatch ? `conv_${convMatch[1]}` : null;

    if (convKey && this.avatarBlobCache.has(convKey)) {
      return of(this.avatarBlobCache.get(convKey));
    }
    if (this.avatarBlobCache.has(url)) {
      return of(this.avatarBlobCache.get(url));
    }

    return this.httpService
      .get(url, {
        responseType: 'blob',
        skipLoader: true,
        skipError: true
      })
      .pipe(
        map((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          this.avatarBlobCache.set(url, blobUrl);
          if (convKey) {
            this.avatarBlobCache.set(convKey, blobUrl);
          }
          return blobUrl;
        }),
        catchError(() => of(url))
      );
  }

  clearAvatarCache(chatId?: string | number): void {
    if (chatId) {
      this.avatarBlobCache.delete(`conv_${chatId}`);
    } else {
      this.avatarBlobCache.clear();
    }
  }

  /**
   * GET /conversations
   * Restituisce la lista paginata delle conversazioni dell'utente autenticato (Bearer token).
   */
  loadChats(
    cursor?: string | null,
    limit = 5,
    search?: string | null
  ): Observable<ApiResponse<Chat[]> & { meta?: ApiCursorMeta }> {
    let url = `${this.baseUrl}?limit=${limit}`;
    if (cursor) {
      url += `&cursor=${encodeURIComponent(cursor)}`;
    }
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }

    return this.httpService.get<ApiConversationListItem[]>(url, { skipLoader: true }).pipe(
      switchMap((res) => {
        const rawList: ApiConversationListItem[] = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(
                (res as unknown as { data?: { data?: ApiConversationListItem[] } })?.data?.data
              )
            ? (res as unknown as { data: { data: ApiConversationListItem[] } }).data.data
            : Array.isArray(res)
              ? (res as unknown as ApiConversationListItem[])
              : [];
        const mappedChats: Chat[] = rawList.map((conv) => this.mapApiConversationToChat(conv));
        const meta =
          (res as unknown as { meta?: ApiCursorMeta })?.meta ||
          (res as unknown as { data?: { meta?: ApiCursorMeta } })?.data?.meta;

        if (mappedChats.length === 0) {
          if (!cursor) {
            this.updateUnreadCountFromChats([], true);
          }
          return of({
            success: res.success,
            message: res.message,
            data: [],
            meta
          });
        }

        const chatObservables$ = mappedChats.map((chat) => {
          if (
            chat.avatarUrl &&
            (chat.avatarUrl.includes('/avatar') || chat.avatarUrl.startsWith('http'))
          ) {
            return this.resolveAvatarBlob(chat.avatarUrl).pipe(
              map((blobUrl) => ({ ...chat, avatarUrl: blobUrl }))
            );
          }
          return of(chat);
        });

        return forkJoin(chatObservables$).pipe(
          map((chats) => {
            this.updateUnreadCountFromChats(chats, !cursor);
            return {
              success: res.success,
              message: res.message,
              data: chats,
              meta
            };
          })
        );
      })
    );
  }

  /**
   * GET /chat/contacts per la rubrica unificata (staff e pazienti collegati al centro)
   * Gli id restituiti sono gli user_id da utilizzare in participant_ids di POST /chat/conversations.
   */
  loadContacts(
    search = '',
    cursor?: string | null,
    limit = 5,
    type?: 'staff' | 'patient'
  ): Observable<ContactsPageResult> {
    let url = `${env.baseUrl}chat/contacts?limit=${limit}`;
    if (search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    if (cursor) {
      url += `&cursor=${encodeURIComponent(cursor)}`;
    }
    if (type) {
      url += `&type=${type}`;
    }

    return this.httpService.get<ApiChatContact[]>(url, { skipLoader: true }).pipe(
      map((res) => {
        const envelope = res as unknown as ContactsApiResponseEnvelope;
        const rawContacts: ApiChatContact[] = Array.isArray(envelope.data)
          ? envelope.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
        const users: User[] = rawContacts.map((c) => ({
          id: String(c.id),
          name: c.name,
          avatar: c.avatar_url || undefined,
          status: 'online'
        }));

        return {
          users,
          contacts: rawContacts,
          nextCursor: envelope.meta?.next_cursor ?? null,
          hasMore: envelope.meta?.has_more ?? false
        };
      })
    );
  }

  /**
   * Metodo di compatibilità per chiamate pregresse basate su pagina numerica
   */
  loadUsers(page = 1, perPage = 30, search = ''): Observable<ApiResponse<UsersPageResult>> {
    return this.loadContacts(search, null, perPage).pipe(
      map((res) => ({
        success: true,
        data: {
          users: res.users,
          total: res.users.length,
          lastPage: res.hasMore ? page + 1 : page
        }
      }))
    );
  }

  /**
   * GET /chat/conversations/{conversation}/messages
   * Pagina i messaggi all'indietro con before_id o in avanti con after_id per sincronizzazione
   */
  loadMessages(
    chatId: string,
    beforeId?: string | number,
    limit = 30,
    afterId?: string | number
  ): Observable<MessagesPageResult> {
    let url = `${this.baseUrl}/${chatId}/messages?limit=${limit}`;
    if (beforeId) {
      url += `&before_id=${beforeId}`;
    } else if (afterId) {
      url += `&after_id=${afterId}`;
    }

    return this.httpService.get<ApiMessagesListResponse>(url, { skipLoader: true }).pipe(
      map((res) => {
        const envelope = res as unknown as MessagesApiResponseEnvelope;
        const innerData =
          envelope?.data && typeof envelope.data === 'object' && !Array.isArray(envelope.data)
            ? (envelope.data as { data?: ApiMessage[] }).data
            : undefined;

        const rawMessages: ApiMessage[] = Array.isArray(envelope?.data)
          ? envelope.data
          : Array.isArray(innerData)
            ? innerData
            : [];

        const hasMore: boolean = Boolean(
          envelope?.meta?.has_more ??
          (envelope?.data as unknown as { meta?: { has_more?: boolean } })?.meta?.has_more
        );

        // Pass 1: Raccogli tutti i messaggi sicuramente inviati dall'utente corrente
        if (this.currentUserId) {
          rawMessages.forEach((m) => {
            if (m.sender?.id && String(m.sender.id) === String(this.currentUserId)) {
              this.rememberOwnMessage(m.id, m.client_uuid);
            }
          });
        }

        // Pass 2: Se tutti i messaggi attivi nella conversazione appartengono all'utente corrente,
        // anche i messaggi eliminati con sender=null appartengono all'utente corrente
        const nonSystemMessages = rawMessages.filter((m) => m.type !== 'system');
        const activeMessages = nonSystemMessages.filter((m) => m.sender?.id);
        const allActiveAreOwn =
          Boolean(this.currentUserId) &&
          activeMessages.length > 0 &&
          activeMessages.every((m) => String(m.sender?.id) === String(this.currentUserId));

        if (allActiveAreOwn && this.currentUserId) {
          rawMessages.forEach((m) => {
            if (m.deleted_at && !m.sender) {
              this.rememberOwnMessage(m.id, m.client_uuid);
            }
          });
        }

        const messages: Message[] = rawMessages.map((m) => this.mapApiMessageToMessage(m));
        return { messages, hasMore };
      })
    );
  }

  /**
   * POST /conversations/{conversation}/read
   * Segna come letta la conversazione fino all'id del messaggio specificato
   */
  markAsRead(
    chatId: string,
    lastMessageId: string | number
  ): Observable<ApiResponse<ApiMarkAsReadResponse>> {
    this.markConversationAsReadLocal(chatId);
    return this.httpService.post<ApiMarkAsReadResponse>(
      `${this.baseUrl}/${chatId}/read`,
      {
        message_id: Number(lastMessageId)
      },
      { skipLoader: true }
    );
  }

  /**
   * POST /conversations
   * Crea una conversazione diretta tra l'utente corrente e il partecipante selezionato,
   * oppure un gruppo con nome obbligatorio se ci sono più partecipanti.
   */
  onNewChat(participantIds: string[], name?: string): Observable<ApiResponse<Chat>> {
    const numericIds = participantIds.map((id) => Number(id)).filter((id) => !isNaN(id));
    const isGroup = numericIds.length > 1;

    const payload: ApiCreateConversationRequest = {
      type: isGroup ? 'group' : 'direct',
      participant_ids: numericIds,
      ...(name ? { name } : {})
    };

    return this.httpService
      .post<ApiConversationListItem>(this.baseUrl, payload, { skipLoader: true })
      .pipe(
        map((res) => ({
          success: res.success,
          message: res.message,
          data: this.mapApiConversationToChat(res.data)
        }))
      );
  }

  /**
   * POST /conversations/{conversation}/messages
   * Invia un messaggio testuale con client_uuid per garantire l'idempotenza
   */
  onSendMessage(chatId: string, content: string): Observable<ApiResponse<Message>> {
    const clientUuid = uuid.v4();
    this.rememberOwnMessage(undefined, clientUuid);

    const payload: ApiSendMessageRequest = {
      client_uuid: clientUuid,
      type: 'text',
      body: content
    };

    return this.httpService
      .post<ApiMessage>(`${this.baseUrl}/${chatId}/messages`, payload, { skipLoader: true })
      .pipe(
        map((res) => {
          if (res.data?.id) {
            this.rememberOwnMessage(res.data.id, clientUuid);
          }
          return {
            success: res.success,
            message: res.message,
            data: this.mapApiMessageToMessage(res.data)
          };
        })
      );
  }

  /**
   * POST /chat/conversations/{conversation}/participants
   * Aggiunge partecipanti a un gruppo (solo admin)
   */
  addParticipants(
    chatId: string | number,
    userIds: (string | number)[]
  ): Observable<ApiResponse<ApiParticipant[]>> {
    const numericIds = userIds.map((id) => Number(id)).filter((id) => !isNaN(id));
    return this.httpService.post<ApiParticipant[]>(
      `${this.baseUrl}/${chatId}/participants`,
      { user_ids: numericIds },
      { skipLoader: true }
    );
  }

  /**
   * GET /chat/conversations/{conversation}
   * Carica il dettaglio della conversazione inclusi i partecipanti attivi
   */
  loadConversationDetail(chatId: string | number): Observable<Chat> {
    return this.httpService
      .get<ApiConversationListItem>(`${this.baseUrl}/${chatId}`, { skipLoader: true })
      .pipe(
        switchMap((res) => {
          const conv = res?.data || (res as unknown as ApiConversationListItem);
          const chat = this.mapApiConversationToChat(conv);
          const convKey = `conv_${chatId}`;
          if (this.avatarBlobCache.has(convKey)) {
            return of({ ...chat, avatarUrl: this.avatarBlobCache.get(convKey) });
          }
          if (
            chat.avatarUrl &&
            (chat.avatarUrl.includes('/avatar') || chat.avatarUrl.startsWith('http'))
          ) {
            return this.resolveAvatarBlob(chat.avatarUrl).pipe(
              map((blobUrl) => ({ ...chat, avatarUrl: blobUrl }))
            );
          }
          return of(chat);
        })
      );
  }

  /**
   * Restituisce i partecipanti della conversazione tramite GET /chat/conversations/{conversation}
   */
  loadParticipants(chatId: string | number): Observable<ChatParticipant[]> {
    return this.loadConversationDetail(chatId).pipe(map((chat) => chat.participants));
  }

  /**
   * DELETE /chat/conversations/{conversation}/participants/{user}
   * Rimuove un partecipante dal gruppo (solo admin) oppure abbandona il gruppo
   */
  removeParticipant(
    chatId: string | number,
    userId: string | number
  ): Observable<ApiResponse<void>> {
    return this.httpService.delete<void>(`${this.baseUrl}/${chatId}/participants/${userId}`, {
      skipLoader: true
    });
  }

  /**
   * PATCH /chat/conversations/{conversation}/participants/{user}
   * Cambia il ruolo di un partecipante tra admin e member (solo admin)
   */
  updateParticipantRole(
    chatId: string | number,
    userId: string | number,
    role: 'admin' | 'member'
  ): Observable<ApiResponse<ApiParticipant>> {
    return this.httpService.patch<ApiParticipant>(
      `${this.baseUrl}/${chatId}/participants/${userId}`,
      { role },
      { skipLoader: true }
    );
  }

  /**
   * PATCH /chat/conversations/{conversation}
   * Aggiorna il nome del gruppo (solo admin)
   */
  updateGroupName(
    chatId: string | number,
    name: string
  ): Observable<ApiResponse<ApiConversationListItem>> {
    return this.httpService.patch<ApiConversationListItem>(
      `${this.baseUrl}/${chatId}`,
      { name },
      { skipLoader: true }
    );
  }

  /**
   * POST /chat/attachments
   * Carica un file allegato o l'avatar del gruppo
   */
  uploadAttachment(file: File): Observable<{ id: number; url?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpService
      .post<{ id: number; url?: string }>(`${env.baseUrl}chat/attachments`, formData, {
        skipLoader: true
      })
      .pipe(map((res) => res.data || (res as unknown as { id: number; url?: string })));
  }

  /**
   * PATCH /chat/conversations/{conversation}
   * Aggiorna l'avatar del gruppo (solo admin)
   */
  updateConversationAvatar(
    chatId: string | number,
    attachmentId: number | null
  ): Observable<ApiResponse<ApiConversationListItem>> {
    return this.httpService.patch<ApiConversationListItem>(
      `${this.baseUrl}/${chatId}`,
      { avatar_attachment_id: attachmentId },
      { skipLoader: true }
    );
  }

  /**
   * PATCH /chat/conversations/{conversation}/mute
   * Silenzia o riattiva le notifiche push della conversazione
   */
  muteConversation(
    chatId: string | number,
    mutedUntil: string | null
  ): Observable<ApiResponse<{ muted_until: string | null }>> {
    return this.httpService.patch<{ muted_until: string | null }>(
      `${this.baseUrl}/${chatId}/mute`,
      { muted_until: mutedUntil },
      { skipLoader: true }
    );
  }

  /**
   * PATCH /chat/messages/{message}
   * Modifica il testo di un messaggio (solo autore)
   */
  updateMessage(messageId: string | number, body: string): Observable<ApiResponse<ApiMessage>> {
    return this.httpService.patch<ApiMessage>(
      `${env.baseUrl}chat/messages/${messageId}`,
      { body },
      { skipLoader: true }
    );
  }

  /**
   * DELETE /chat/messages/{message}
   * Elimina un messaggio (soft-delete, solo autore o admin)
   */
  deleteMessage(messageId: string | number): Observable<ApiResponse<void>> {
    return this.httpService.delete<void>(`${env.baseUrl}chat/messages/${messageId}`, {
      skipLoader: true
    });
  }

  // --- MAPPERS ---

  private getFormattedUserName(u?: Record<string, unknown> | null): string | undefined {
    if (!u) return undefined;
    const firstName = ((u['first_name'] || u['nome']) as string | undefined)?.trim();
    const lastName = ((u['last_name'] || u['cognome']) as string | undefined)?.trim();
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName || lastName) {
      return (firstName || lastName) as string;
    }
    const name = ((u['name'] || u['full_name'] || u['nomeCompleto']) as string | undefined)?.trim();
    return name || undefined;
  }

  public mapApiParticipantToParticipant(
    p: ApiParticipant | Record<string, unknown>
  ): ChatParticipant {
    const rawUser = ((p as ApiParticipant).user || p) as unknown as Record<string, unknown>;
    const role = (p as ApiParticipant).role || (p as Record<string, unknown>)['role'] || 'member';
    const formattedName = this.getFormattedUserName(rawUser) || 'Utente';
    return {
      id: String(rawUser['id'] || ''),
      name: formattedName,
      avatar: (rawUser['avatar_url'] as string) || undefined,
      status: 'online',
      role: role as 'admin' | 'member',
      joinedAt: (p as ApiParticipant).joined_at
        ? new Date((p as ApiParticipant).joined_at)
        : undefined,
      leftAt: (p as ApiParticipant).left_at
        ? new Date((p as ApiParticipant).left_at as string)
        : null
    };
  }

  public mapApiConversationToChat(conv: ApiConversationListItem): Chat {
    const directParticipant = conv.other_participant as unknown as
      Record<string, unknown> | undefined;
    const directParticipantName =
      conv.type === 'direct' ? this.getFormattedUserName(directParticipant) : undefined;

    const chatName =
      conv.type === 'direct'
        ? directParticipantName || conv.name || undefined
        : conv.name || undefined;

    const participants: ChatParticipant[] = (conv.participants || []).map((p) =>
      this.mapApiParticipantToParticipant(p)
    );

    if (
      conv.other_participant &&
      !participants.some((p) => p.id === String(conv.other_participant?.id))
    ) {
      participants.push({
        id: String(conv.other_participant.id),
        name: directParticipantName || 'Utente',
        avatar: conv.other_participant.avatar_url || undefined,
        status: 'online',
        role: 'member'
      });
    }

    const lastMessage = conv.last_message
      ? this.mapApiMessageToMessage(conv.last_message)
      : undefined;

    const directAvatar = conv.other_participant?.avatar_url || undefined;
    const avatarUrl = conv.avatar_url || directAvatar || undefined;

    return {
      id: String(conv.id),
      name: chatName,
      avatarUrl,
      type: conv.type,
      participants,
      lastMessage,
      unreadCount: Number(conv.unread_count) || 0,
      createdAt: new Date(conv.created_at),
      mutedUntil: conv.muted_until,
      createdBy: conv.created_by
    };
  }

  public mapApiMessageToMessage(msg: ApiMessage): Message {
    let isOwn = false;
    let senderId = '';

    if (msg.sender?.id) {
      senderId = String(msg.sender.id);
      if (this.currentUserId && senderId === String(this.currentUserId)) {
        isOwn = true;
        this.rememberOwnMessage(msg.id, msg.client_uuid);
      }
    } else if (msg.type !== 'system') {
      // Messaggio eliminato o con sender non fornito
      if (this.isOwnMessageIdOrUuid(msg.id, msg.client_uuid)) {
        isOwn = true;
        senderId = this.currentUserId || '';
        this.rememberOwnMessage(msg.id, msg.client_uuid);
      }
    }

    return {
      id: String(msg.id),
      chatId: String(msg.conversation_id),
      senderId,
      content: msg.body || (msg.attachments?.length ? '[Allegato]' : ''),
      timestamp: new Date(msg.created_at),
      read: true,
      type: msg.type,
      editedAt: msg.edited_at,
      deletedAt: msg.deleted_at,
      isOwn
    };
  }

  private mapApiUserSummaryToUser(u: ApiUserSummary): User {
    return {
      id: String(u.id),
      name: u.name || 'Utente',
      avatar: u.avatar_url || undefined,
      status: 'online'
    };
  }
}
